import axios from "axios";
import { supabase } from "./supabaseClient";
import { UrlScanResponse } from "../models/url-analysis-data-structures";

// --- URL Scan API ---
export async function scanUrlApi(
  url: string,
  timeoutMs = 30000,
): Promise<UrlScanResponse> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const params = new URLSearchParams({ url: url.trim() });
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/scan-url?${params.toString()}`, {
      method: "POST",
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    return await response.json();
  } finally {
    clearTimeout(timeout);
  }
}

export const getToken = async () => {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  return session?.access_token;
};

export interface AnalysisInfo {
  analysis_id: string;
  sha256: string;
  file_size: number;
  filename: string;
  object_type: string;
  rating: number | null;
  rating_flag: string;
  timestamp: string;
  scan_time_ms: string;
  is_public: boolean;
  is_favorite: boolean;
}

const getAuthHeaders = async () => {
  const token = await getToken();
  const headers: Record<string, string> = {};

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

interface GetRecentAnalysesParams {
  limit?: number;
}

export const getRecentAnalyses = async ({
  limit = 100,
}: GetRecentAnalysesParams = {}) => {
  const headers = await getAuthHeaders();
  const resp = await axios.get("/api/analyses/recent", {
    headers,
    params: { limit },
  });

  const { analyses }: { analyses: AnalysisInfo[] } = resp.data;

  return { analyses };
};

interface GetUserAnalysesParams {
  limit?: number;
}

export const getUserAnalyses = async ({
  limit = 100,
}: GetUserAnalysesParams = {}) => {
  const headers = await getAuthHeaders();
  const resp = await axios.get("/api/user/analyses", {
    headers,
    params: { limit },
  });

  const { analyses }: { analyses: AnalysisInfo[] } = resp.data;

  return { analyses };
};

export const getFavoriteAnalyses = async () => {
  const headers = await getAuthHeaders();
  const resp = await axios.get("/api/user/favorites", {
    headers,
  });

  const { analyses }: { analyses: AnalysisInfo[] } = resp.data;

  return { analyses };
};

export const getAvailableObjectTypes = async () => {
  const { analyses } = await getRecentAnalyses({ limit: 100 });

  const availableTypes = Array.from(
    new Set(analyses.map((analysis) => analysis.object_type)),
  ).sort();

  return { availableTypes };
};

interface SearchAnalysesParams {
  limit?: number;
  query?: string;
  types?: string[];
}

export const searchAnalyses = async ({
  limit = 100,
  query = "",
  types = [],
}: SearchAnalysesParams) => {
  const headers = await getAuthHeaders();

  const params = new URLSearchParams();
  params.append("limit", limit.toString());

  if (/^[a-fA-F0-9]{64}$/.test(query.trim())) {
    params.append("sha256", query.toLocaleLowerCase().trim());
  } else if (query) {
    params.append("filename", query);
  }
  types.forEach((type) => params.append("object_type", type));

  const resp = await axios.get("/api/analyses/search", { headers, params });

  const { analyses }: { analyses: AnalysisInfo[] } = resp.data;

  return { analyses };
};

interface GetAnalysisParams {
  analysisId: string;
}

export const getAnalysisInfo = async ({
  analysisId,
}: GetAnalysisParams): Promise<AnalysisInfo> => {
  const headers = await getAuthHeaders();

  const resp = await axios.get(`/api/analysis/${analysisId}/summary`, {
    headers,
    validateStatus: () => true,
  });

  if (resp.status === 404) {
    throw new Error("Analysis not found");
  }

  if (resp.status !== 200) {
    throw new Error("Failed fetching analysis");
  }

  return resp.data;
};

export const getAnalysis = async ({ analysisId }: GetAnalysisParams) => {
  const headers = await getAuthHeaders();

  const resp = await axios.get(`/api/analysis/${analysisId}/report`, {
    headers,
    validateStatus: () => true,
  });

  if (resp.status === 404) {
    throw new Error("Analysis not found");
  }

  if (resp.status !== 200) {
    throw new Error("Failed fetching analysis");
  }

  return { report: resp.data };
};

export interface StatsData {
  total_files: number;
  avg_scan_time_ms: number;
  median_scan_time_ms: number;
  median_scan_time_per_mb: number;
  high_risk_percentage: number;
  most_common_file_type: string;
  most_common_file_type_percentage: number;
}

export const getStats = async (): Promise<StatsData> => {
  const headers = await getAuthHeaders();

  const resp = await axios.get("/api/stats", { headers });

  if (resp.status !== 200) {
    throw new Error("failed fetching stats");
  }

  return resp.data;
};

interface ChatMessage {
  role: "assistant" | "user";
  content: string;
}

interface ChatParams {
  messages: ChatMessage[];
  analysisId: string;
}

interface ChatResponse {
  reply: string;
}

export const chat = async ({
  messages,
  analysisId,
}: ChatParams): Promise<ChatResponse> => {
  const headers = await getAuthHeaders();

  const data = {
    messages,
    analysis_id: analysisId,
  };

  const resp = await axios.post("/api/chat", data, { headers });

  if (resp.status !== 200) {
    throw new Error("Chat failed");
  }

  return resp.data;
};

export const toggleVisibility = async (
  analysisId: string,
  isPublic: boolean,
): Promise<{ analysis_id: string; is_public: boolean }> => {
  const headers = await getAuthHeaders();

  const resp = await axios.patch(
    `/api/analysis/${analysisId}/visibility`,
    { is_public: isPublic },
    { headers },
  );

  return resp.data;
};

export const toggleFavorite = async (
  analysisId: string,
  isFavorite: boolean,
): Promise<{ analysis_id: string; is_favorite: boolean }> => {
  const headers = await getAuthHeaders();

  const resp = await axios.put(
    `/api/analysis/${analysisId}/favorite`,
    { is_favorite: isFavorite },
    { headers },
  );

  return resp.data;
};

const apiModule = {
  getAnalysis,
  getRecentAnalyses,
  getUserAnalyses,
  getFavoriteAnalyses,
  searchAnalyses,
  toggleVisibility,
  toggleFavorite,
  chat,
};

export default apiModule;
